// Per-profession experience helpers.
//
// Expert experience now lives per profession as tenure (start/end dates +
// "to present" / isActive) on `pro_professions`, not as a single global
// `yearsExperience` band. These helpers derive years from that tenure and format
// the expert-card subtitle, mirroring the backend scorer's formula in
// application/src/lib/controllers/brandProjects.js (getProjectExpertScores):
//   end  = (isActive || !endDate) ? now : endDate
//   years = max(0, (end - start) / MS_PER_YEAR)

export interface ProfessionTenure {
  profession?: { name?: string | null } | null;
  subProfession?: { name?: string | null } | null;
  startDate?: string | null;
  endDate?: string | null;
  isActive?: boolean;
}

const MS_PER_YEAR = 365.25 * 24 * 60 * 60 * 1000;

/**
 * Years of tenure for a single profession. Returns null when there's no usable
 * start date (un-backfilled rows) so callers can show the name without a number.
 */
export function getProfessionYears(p?: ProfessionTenure | null): number | null {
  if (!p?.startDate) return null;
  const start = new Date(p.startDate).getTime();
  if (!Number.isFinite(start)) return null;
  const end =
    p.isActive || !p.endDate ? Date.now() : new Date(p.endDate).getTime();
  if (!Number.isFinite(end)) return null;
  return Math.max(0, (end - start) / MS_PER_YEAR);
}

/** "5 yrs" / "1 yr" / "< 1 yr"; null when there's no usable tenure. */
export function formatYears(years: number | null): string | null {
  if (years === null || !Number.isFinite(years)) return null;
  if (years < 1) return '< 1 yr';
  const rounded = Math.round(years);
  return rounded === 1 ? '1 yr' : `${rounded} yrs`;
}

/** Sub-profession name if present, else the profession name. */
export function professionName(p?: ProfessionTenure | null): string | null {
  return p?.subProfession?.name || p?.profession?.name || null;
}

/** A single profession formatted as "Name · 5 yrs" (or just "Name" if no tenure). */
export function formatProfessionWithExperience(
  p?: ProfessionTenure | null
): string | null {
  const name = professionName(p);
  if (!name) return null;
  const yrs = formatYears(getProfessionYears(p));
  return yrs ? `${name} · ${yrs}` : name;
}

/**
 * The expert-card subtitle: each profession with its own tenure-derived years,
 * e.g. "Makeup Artist · 5 yrs | Hair Stylist · 3 yrs". Professions without
 * tenure show just their name; returns "" when there's nothing to show.
 */
export function formatProfessionsWithExperience(
  professions?: ProfessionTenure[] | null
): string {
  if (!professions?.length) return '';
  return professions
    .map(formatProfessionWithExperience)
    .filter(Boolean)
    .join(' | ');
}
